import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  ReceiptPercentIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import analyticsApi, { 
  DashboardAnalytics, 
  DashboardTransaction, 
  CustomerAnalytics 
} from '../api/analytics';
import FinancialHealthMetricsComponent from '../components/FinancialHealthMetrics';
import PageHero from '../components/ui/PageHero';

// Remove old interfaces and use the ones from analytics API
interface FinancialStats extends DashboardAnalytics {}

interface Transaction extends DashboardTransaction {}

interface Customer extends CustomerAnalytics {}

type TabType = 'overview' | 'revenue' | 'expenses' | 'cashflow';

export default function FinancialDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'7days' | '30days' | '90days' | '12months' | '24months' | 'all'>('all');
  const [financialStats, setFinancialStats] = useState<FinancialStats>({
    totalRevenue: 0,
    totalExpenses: 0,
    totalPurchases: 0,
    netProfit: 0,
    profitMargin: 0,
    monthlyRevenue: {},
    monthlyExpenses: {},
    monthlyPurchases: {},
    recentTransactions: [],
    topCustomers: [],
    expenseCategories: {},
    cashFlow: { incoming: 0, outgoing: 0, balance: 0 },
    kpis: { revenueGrowth: 0, customerAcquisition: 0, avgOrderValue: 0, monthlyRecurring: 0 },
  });

  useEffect(() => {
    loadFinancialData();
  }, [dateRange]);

  const loadFinancialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use real API instead of mock data
      const dashboardData = await analyticsApi.getDashboardAnalytics(dateRange);
      setFinancialStats(dashboardData);
    } catch (error) {
      console.error('Error loading financial data:', error);
      setError('Failed to load financial data. Please try again.');
      
      // Set empty data on error
      setFinancialStats({
        totalRevenue: 0,
        totalExpenses: 0,
        totalPurchases: 0,
        netProfit: 0,
        profitMargin: 0,
        monthlyRevenue: {},
        monthlyExpenses: {},
        monthlyPurchases: {},
        recentTransactions: [],
        topCustomers: [],
        expenseCategories: {},
        cashFlow: { incoming: 0, outgoing: 0, balance: 0 },
        kpis: { revenueGrowth: 0, customerAcquisition: 0, avgOrderValue: 0, monthlyRecurring: 0 },
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'revenue', name: 'Revenue', icon: BanknotesIcon },
    { id: 'expenses', name: 'Expenses', icon: ReceiptPercentIcon },
    { id: 'cashflow', name: 'Cash Flow', icon: CurrencyDollarIcon },
  ];

  const getMainKPIs = () => [
    {
      name: 'Total Revenue',
      href: '/sales',
      icon: BanknotesIcon,
      stat: `R${(financialStats?.totalRevenue || 0).toLocaleString()}`,
      change: `+${(financialStats?.kpis?.revenueGrowth || 0).toFixed(1)}%`,
      changeType: (financialStats?.kpis?.revenueGrowth || 0) >= 0 ? 'increase' : 'decrease'
    },
    {
      name: 'Total Expenses',
      href: '/expenses',
      icon: ReceiptPercentIcon,
      stat: `R${(financialStats?.totalExpenses || 0).toLocaleString()}`,
      change: `${(((financialStats?.totalExpenses || 0) / Math.max(financialStats?.totalRevenue || 1, 1)) * 100).toFixed(1)}% of revenue`,
      changeType: 'neutral'
    },
    {
      name: 'Total Purchases',
      href: '/purchases',
      icon: ShoppingCartIcon,
      stat: `R${(financialStats?.totalPurchases || 0).toLocaleString()}`,
      change: `${(financialStats?.totalPurchases || 0) > 0 ? 'Inventory & stock' : 'No purchases'}`,
      changeType: 'neutral'
    },
    {
      name: 'Net Profit',
      href: '/accounting',
      icon: ChartBarIcon,
      stat: `R${(financialStats?.netProfit || 0).toLocaleString()}`,
      change: `${(financialStats?.netProfit || 0) >= 0 ? '+' : ''}${(financialStats?.profitMargin || 0).toFixed(1)}%`,
      changeType: (financialStats?.netProfit || 0) >= 0 ? 'increase' : 'decrease'
    },
    {
      name: 'Cash Flow',
      href: '/cash-flow',
      icon: CurrencyDollarIcon,
      stat: `R${(financialStats?.cashFlow?.balance || 0).toLocaleString()}`,
      change: `${(financialStats?.cashFlow?.balance || 0) >= 0 ? 'Positive' : 'Negative'}`,
      changeType: (financialStats?.cashFlow?.balance || 0) >= 0 ? 'increase' : 'decrease'
    },
    {
      name: 'Monthly Recurring',
      href: '/recurring-revenue',
      icon: ArrowTrendingUpIcon,
      stat: `R${(financialStats?.kpis?.monthlyRecurring || 0).toLocaleString()}`,
      change: `${(financialStats?.kpis?.customerAcquisition || 0)} customers`,
      changeType: 'increase'
    },
  ];

  // Helper function to get number of months to display based on date range
  const getMonthsToDisplay = (dateRange: string) => {
    switch (dateRange) {
      case '7days':
      case '30days':
        return 3; // Show last 3 months for short periods
      case '90days':
        return 6; // Show last 6 months for 90 days
      case '12months':
        return 12; // Show last 12 months for 1 year
      case '24months':
        return 24; // Show last 24 months for 2 years
      case 'all':
        return -1; // Show all available months
      default:
        return 6; // Default to 6 months
    }
  };

  const monthsToDisplay = getMonthsToDisplay(dateRange);
  const getMonthlyData = (data: Record<string, number>) => {
    const entries = Object.entries(data);
    return monthsToDisplay === -1 ? entries : entries.slice(-monthsToDisplay);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Dashboard</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={loadFinancialData}
                className="bg-red-100 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Financial Dashboard"
        subtitle="Overview of your business financial performance"
        actions={
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
            className="input w-auto min-w-[10rem] text-sm"
          >
            <option value="7days">Last 7 days</option>
            <option value="30days">Last 30 days</option>
            <option value="90days">Last 90 days</option>
            <option value="12months">Last 12 months</option>
            <option value="24months">Last 24 months</option>
            <option value="all">All data</option>
          </select>
        }
      />

      {/* Main KPIs */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {getMainKPIs().map((item) => (
          <Link
            key={item.name}
            to={item.href}
            className="group flex h-full flex-col gap-3 rounded-lg bg-white p-4 shadow transition-shadow hover:shadow-lg sm:p-5"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-cyan-500">
              <item.icon className="h-5 w-5 text-white" aria-hidden="true" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <p className="text-sm font-medium leading-snug text-slate-500">
                {item.name}
              </p>
              <p className="text-2xl font-semibold leading-tight tracking-tight text-slate-900 break-words">
                {item.stat}
              </p>
              <p
                className={`mt-auto flex items-start gap-1 text-sm font-semibold leading-snug ${
                  item.changeType === 'increase'
                    ? 'text-green-600'
                    : item.changeType === 'decrease'
                      ? 'text-red-600'
                      : 'text-slate-600'
                }`}
              >
                {item.changeType === 'increase' && (
                  <ArrowUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-500" aria-hidden="true" />
                )}
                {item.changeType === 'decrease' && (
                  <ArrowDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
                )}
                <span className="sr-only">
                  {item.changeType === 'increase'
                    ? 'Increased'
                    : item.changeType === 'decrease'
                      ? 'Decreased'
                      : 'No change'}{' '}
                  by
                </span>
                <span className="min-w-0">{item.change}</span>
              </p>
            </div>
          </Link>
        ))}
      </div>

      {/* Tabs */}
      <div className="mt-8">
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full rounded-md border-gray-300 focus:border-cyan-500 focus:ring-cyan-500"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as TabType)}
          >
            {tabs.map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-cyan-500 text-cyan-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <>
            {/* Financial Performance Charts */}
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Revenue vs Expenses Chart */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                    Revenue, Expenses &amp; Purchases (Monthly)
                  </h3>
                  <div className="space-y-3">
                    {getMonthlyData(financialStats?.monthlyRevenue || {}).map(([month, revenue]) => {
                      const expenses = financialStats?.monthlyExpenses?.[month] || 0;
                      const purchases = financialStats?.monthlyPurchases?.[month] || 0;
                      const maxValue = Math.max(revenue, expenses, purchases);
                      return (
                        <div key={month} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-slate-700">{month}</span>
                            <div className="flex items-center space-x-4">
                              <span className="text-green-600">R{(revenue || 0).toLocaleString()}</span>
                              <span className="text-red-600">R{(expenses || 0).toLocaleString()}</span>
                              <span className="text-purple-600">R{(purchases || 0).toLocaleString()}</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: maxValue > 0 ? `${((revenue || 0) / maxValue) * 100}%` : '0%' }}
                              />
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-red-600 h-2 rounded-full"
                                style={{ width: maxValue > 0 ? `${((expenses || 0) / maxValue) * 100}%` : '0%' }}
                              />
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-purple-600 h-2 rounded-full"
                                style={{ width: maxValue > 0 ? `${((purchases || 0) / maxValue) * 100}%` : '0%' }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Object.keys(financialStats?.monthlyRevenue || {}).length === 0 && (
                    <p className="text-slate-500 text-sm">No revenue data available for the selected period</p>
                  )}
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-600 rounded-full mr-1"></div>
                      Revenue
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-600 rounded-full mr-1"></div>
                      Expenses
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-purple-600 rounded-full mr-1"></div>
                      Purchases
                    </div>
                  </div>
                </div>
              </div>

              {/* Expense Categories */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                    Expense Categories
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(financialStats?.expenseCategories || {})
                      .sort(([,a], [,b]) => (b || 0) - (a || 0))
                      .map(([category, amount]) => {
                        const safeAmount = amount || 0;
                        const totalExpenses = financialStats?.totalExpenses || 0;
                        const percentage = totalExpenses > 0 ? (safeAmount / totalExpenses) * 100 : 0;
                        return (
                          <div key={category} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="font-medium text-slate-700">{category}</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-slate-900">R{safeAmount.toLocaleString()}</span>
                                <span className="text-slate-500">({percentage.toFixed(1)}%)</span>
                              </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-cyan-600 h-2 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {Object.keys(financialStats?.expenseCategories || {}).length === 0 && (
                    <p className="text-slate-500 text-sm">No expense data available</p>
                  )}
                </div>
              </div>

              {/* Cash Flow Status */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                    Cash Flow Status
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center">
                        <ArrowUpIcon className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-900">Money In</span>
                      </div>
                      <span className="text-lg font-semibold text-green-900">
                        R{(financialStats?.cashFlow?.incoming || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <ArrowDownIcon className="h-5 w-5 text-red-600 mr-2" />
                          <span className="text-sm font-medium text-red-900">Money Out</span>
                        </div>
                        <span className="text-lg font-semibold text-red-900">
                          R{(financialStats?.cashFlow?.outgoing || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className="mt-1 ml-7 text-xs text-red-700/80">
                        Expenses R{(financialStats?.totalExpenses || 0).toLocaleString()} • Purchases R{(financialStats?.totalPurchases || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-cyan-50 rounded-lg">
                      <div className="flex items-center">
                        <CurrencyDollarIcon className={`h-5 w-5 ${(financialStats?.cashFlow?.balance || 0) >= 0 ? 'text-blue-400' : 'text-red-400'}`} />
                        <span className="text-sm font-medium text-blue-900">Net Cash Flow</span>
                      </div>
                      <dd className={`text-lg font-medium ${(financialStats?.cashFlow?.balance || 0) >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                        R{(financialStats?.cashFlow?.balance || 0).toLocaleString()}
                      </dd>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                    Recent Transactions
                  </h3>
                  <div className="space-y-3">
                    {(!financialStats?.recentTransactions || financialStats.recentTransactions.length === 0) ? (
                      <p className="text-slate-500 text-center py-8">No recent transactions</p>
                    ) : (
                      financialStats.recentTransactions.slice(0, 5).map((transaction) => (
                        <div key={transaction.id} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div className={`w-2 h-2 rounded-full mr-3 ${
                                transaction.type === 'income' ? 'bg-green-500' : 'bg-red-500'
                              }`} />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{transaction.description || 'Transaction'}</p>
                                <p className="text-xs text-slate-500">{transaction.category || 'General'} • {new Date(transaction.date).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${
                              transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.type === 'income' ? '+' : '-'}R{(transaction.amount || 0).toLocaleString()}
                            </p>
                            <p className={`text-xs ${
                              transaction.status === 'completed' 
                                ? 'text-green-500' 
                                : transaction.status === 'pending' 
                                  ? 'text-yellow-500' 
                                  : 'text-red-500'
                            }`}>
                              {transaction.status || 'unknown'}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {(financialStats?.recentTransactions?.length || 0) > 5 && (
                    <div className="mt-4">
                      <Link
                        to="/transactions"
                        className="text-sm text-cyan-600 hover:text-cyan-500"
                      >
                        View all transactions →
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Top Customers */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">
                    Top Customers
                  </h3>
                  <div className="space-y-3">
                    {(!financialStats?.topCustomers || financialStats.topCustomers.length === 0) ? (
                      <p className="text-slate-500 text-sm">No customer data available</p>
                    ) : (
                      financialStats.topCustomers.map((customer, index) => (
                        <div key={customer.id} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-semibold mr-3">
                              {index + 1}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{customer.name || 'Unknown Customer'}</p>
                              <p className="text-xs text-slate-500">
                                {customer.transactionCount || 0} transaction{(customer.transactionCount || 0) !== 1 ? 's' : ''} • Last: {new Date(customer.lastPurchase).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-slate-900">R{(customer.totalSpent || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Health Metrics */}
            <div className="mt-8">
              <FinancialHealthMetricsComponent />
            </div>
          </>
        )}

        {/* Other tab content would go here */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <BanknotesIcon className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Total Revenue</dt>
                        <dd className="text-lg font-medium text-slate-900">R{(financialStats?.totalRevenue || 0).toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ArrowTrendingUpIcon className="h-6 w-6 text-blue-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Average Order Value</dt>
                        <dd className="text-lg font-medium text-slate-900">R{(financialStats?.kpis?.avgOrderValue || 0).toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartBarIcon className="h-6 w-6 text-purple-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Revenue Growth</dt>
                        <dd className="text-lg font-medium text-slate-900">+{(financialStats?.kpis?.revenueGrowth || 0).toFixed(1)}%</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Trends Chart */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Monthly Revenue Trends</h3>
                <div className="space-y-4">
                  {getMonthlyData(financialStats.monthlyRevenue).map(([month, revenue]) => {
                    const maxRevenue = Math.max(...Object.values(financialStats.monthlyRevenue));
                    const percentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                    return (
                      <div key={month} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">{month}</span>
                          <span className="text-sm font-semibold text-green-600">R{revenue.toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-green-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {Object.keys(financialStats.monthlyRevenue).length === 0 && (
                  <p className="text-slate-500 text-center py-8">No revenue data available for the selected period</p>
                )}
              </div>
            </div>

            {/* Top Customers by Revenue */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Top Revenue Contributors</h3>
                <div className="space-y-3">
                  {financialStats.topCustomers.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No customer revenue data available</p>
                  ) : (
                    financialStats.topCustomers.map((customer, index) => {
                      const maxSpent = Math.max(...financialStats.topCustomers.map(c => c.totalSpent));
                      const percentage = maxSpent > 0 ? (customer.totalSpent / maxSpent) * 100 : 0;
                      return (
                        <div key={customer.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">
                                {index + 1}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{customer.name}</p>
                                <p className="text-xs text-slate-500">{customer.transactionCount} transactions</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-semibold text-green-600">R{customer.totalSpent.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">Last: {new Date(customer.lastPurchase).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Revenue by Transaction Type */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Revenue Breakdown</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-md font-medium text-slate-700 mb-3">Key Metrics</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="text-sm font-medium text-green-900">Monthly Recurring</span>
                        <span className="text-lg font-semibold text-green-900">R{financialStats.kpis.monthlyRecurring.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-cyan-50 rounded-lg">
                        <span className="text-sm font-medium text-blue-900">Customer Acquisition</span>
                        <span className="text-lg font-semibold text-blue-900">{financialStats.kpis.customerAcquisition}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <span className="text-sm font-medium text-purple-900">Profit Margin</span>
                        <span className="text-lg font-semibold text-purple-900">{financialStats.profitMargin.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-md font-medium text-slate-700 mb-3">Performance Indicators</h4>
                    <div className="space-y-3">
                      <div className="p-3 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-slate-600">Revenue Target Achievement</span>
                          <span className="text-sm font-semibold text-slate-900">85%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '85%' }} />
                        </div>
                      </div>
                      <div className="p-3 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-slate-600">Customer Retention</span>
                          <span className="text-sm font-semibold text-slate-900">92%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: '92%' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="space-y-6">
            {/* Expense Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ReceiptPercentIcon className="h-6 w-6 text-red-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Total Expenses</dt>
                        <dd className="text-lg font-medium text-slate-900">R{(financialStats?.totalExpenses || 0).toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartBarIcon className="h-6 w-6 text-orange-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Categories</dt>
                        <dd className="text-lg font-medium text-slate-900">{Object.keys(financialStats?.expenseCategories || {}).length}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ArrowTrendingUpIcon className="h-6 w-6 text-yellow-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Expense Ratio</dt>
                        <dd className="text-lg font-medium text-slate-900">{(((financialStats?.totalExpenses || 0) / Math.max(financialStats?.totalRevenue || 1, 1)) * 100).toFixed(1)}%</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Expense Categories Breakdown */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Expense Categories Breakdown</h3>
                <div className="space-y-4">
                  {Object.entries(financialStats.expenseCategories)
                    .sort(([,a], [,b]) => b - a)
                    .map(([category, amount], index) => {
                      const percentage = financialStats.totalExpenses > 0 ? (amount / financialStats.totalExpenses) * 100 : 0;
                      const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-cyan-500', 'bg-cyan-500', 'bg-purple-500'];
                      const colorClass = colors[index % colors.length];
                      
                      return (
                        <div key={category} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-4 h-4 ${colorClass} rounded-full mr-3`}></div>
                              <span className="text-sm font-medium text-slate-700">{category}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-sm font-semibold text-slate-900">R{amount.toLocaleString()}</span>
                              <span className="text-xs text-slate-500 ml-2">({percentage.toFixed(1)}%)</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className={`${colorClass} h-3 rounded-full transition-all duration-300`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
                {Object.keys(financialStats.expenseCategories).length === 0 && (
                  <p className="text-slate-500 text-center py-8">No expense category data available</p>
                )}
              </div>
            </div>

            {/* Monthly Expense Trends */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Monthly Expense Trends</h3>
                <div className="space-y-4">
                  {getMonthlyData(financialStats.monthlyExpenses).map(([month, expenses]) => {
                    const revenue = financialStats.monthlyRevenue[month] || 0;
                    const expenseRatio = revenue > 0 ? (expenses / revenue) * 100 : 0;
                    const maxExpenses = Math.max(...Object.values(financialStats.monthlyExpenses));
                    const percentage = maxExpenses > 0 ? (expenses / maxExpenses) * 100 : 0;
                    
                    return (
                      <div key={month} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700">{month}</span>
                          <div className="text-right">
                            <span className="text-sm font-semibold text-red-600">R{expenses.toLocaleString()}</span>
                            <span className="text-xs text-slate-500 block">
                              {expenseRatio.toFixed(1)}% of revenue
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-red-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {Object.keys(financialStats.monthlyExpenses).length === 0 && (
                  <p className="text-slate-500 text-center py-8">No expense trend data available</p>
                )}
              </div>
            </div>

            {/* Cost Control Metrics */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Cost Control Analysis</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-md font-medium text-slate-700 mb-3">Expense Efficiency</h4>
                    <div className="space-y-3">
                      <div className="p-3 bg-cyan-50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-blue-900">Operating Efficiency</span>
                          <span className="text-lg font-semibold text-blue-900">
                            {financialStats.totalRevenue > 0 ? (((financialStats.totalRevenue - financialStats.totalExpenses) / financialStats.totalRevenue) * 100).toFixed(1) : '0'}%
                          </span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div 
                            className="bg-cyan-600 h-2 rounded-full" 
                            style={{ 
                              width: `${financialStats.totalRevenue > 0 ? Math.min(((financialStats.totalRevenue - financialStats.totalExpenses) / financialStats.totalRevenue) * 100, 100) : 0}%` 
                            }} 
                          />
                        </div>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-yellow-900">Cost Control Target</span>
                          <span className="text-lg font-semibold text-yellow-900">75%</span>
                        </div>
                        <div className="w-full bg-yellow-200 rounded-full h-2">
                          <div className="bg-yellow-600 h-2 rounded-full" style={{ width: '75%' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-md font-medium text-slate-700 mb-3">Top Expense Areas</h4>
                    <div className="space-y-2">
                      {Object.entries(financialStats.expenseCategories)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 3)
                        .map(([category, amount], index) => (
                          <div key={category} className="flex justify-between items-center p-2 border border-gray-200 rounded">
                            <span className="text-sm text-slate-700">{category}</span>
                            <span className="text-sm font-semibold text-red-600">R{amount.toLocaleString()}</span>
                          </div>
                        ))}
                      {Object.keys(financialStats.expenseCategories).length === 0 && (
                        <p className="text-slate-500 text-sm">No expense data available</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cashflow' && (
          <div className="space-y-6">
            {/* Cash Flow Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ArrowUpIcon className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Cash Inflow</dt>
                        <dd className="text-lg font-medium text-slate-900">R{(financialStats?.cashFlow?.incoming || 0).toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ArrowDownIcon className="h-6 w-6 text-red-400" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Cash Outflow</dt>
                        <dd className="text-lg font-medium text-slate-900">R{(financialStats?.cashFlow?.outgoing || 0).toLocaleString()}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CurrencyDollarIcon className={`h-6 w-6 ${(financialStats?.cashFlow?.balance || 0) >= 0 ? 'text-blue-400' : 'text-red-400'}`} />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-slate-500 truncate">Net Cash Flow</dt>
                        <dd className={`text-lg font-medium ${(financialStats?.cashFlow?.balance || 0) >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                          R{(financialStats?.cashFlow?.balance || 0).toLocaleString()}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Cash Flow Visualization */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Cash Flow Analysis</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-md font-medium text-slate-700 mb-3">Inflow vs Outflow</h4>
                    <div className="space-y-4">
                      <div className="relative">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-green-700">Money Coming In</span>
                          <span className="text-sm font-semibold text-green-800">R{(financialStats?.cashFlow?.incoming || 0).toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-6">
                          <div 
                            className="bg-green-500 h-6 rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max(((financialStats?.cashFlow?.incoming || 0) / Math.max(Math.max(financialStats?.cashFlow?.incoming || 0, financialStats?.cashFlow?.outgoing || 0), 1)) * 100, 10)}%` 
                            }}
                          >
                            <span className="text-xs text-white font-medium">
                              {(((financialStats?.cashFlow?.incoming || 0) / Math.max((financialStats?.cashFlow?.incoming || 0) + (financialStats?.cashFlow?.outgoing || 0), 1)) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="relative">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-red-700">Money Going Out</span>
                          <span className="text-sm font-semibold text-red-800">R{(financialStats?.cashFlow?.outgoing || 0).toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-6">
                          <div 
                            className="bg-red-500 h-6 rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max(((financialStats?.cashFlow?.outgoing || 0) / Math.max(Math.max(financialStats?.cashFlow?.incoming || 0, financialStats?.cashFlow?.outgoing || 0), 1)) * 100, 10)}%` 
                            }}
                          >
                            <span className="text-xs text-white font-medium">
                              {(((financialStats?.cashFlow?.outgoing || 0) / Math.max((financialStats?.cashFlow?.incoming || 0) + (financialStats?.cashFlow?.outgoing || 0), 1)) * 100).toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-medium text-slate-700 mb-3">Cash Position</h4>
                    <div className="text-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
                      <div className={`text-4xl font-bold ${(financialStats?.cashFlow?.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(financialStats?.cashFlow?.balance || 0) >= 0 ? '+' : ''}R{(financialStats?.cashFlow?.balance || 0).toLocaleString()}
                      </div>
                      <p className="text-sm text-slate-600 mt-2">
                        {(financialStats?.cashFlow?.balance || 0) >= 0 ? 'Positive Cash Flow' : 'Negative Cash Flow'}
                      </p>
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                        (financialStats?.cashFlow?.balance || 0) >= 0 
                          ? 'bg-emerald-100 text-emerald-800' 
                          : 'bg-rose-100 text-rose-800'
                      }`}>
                        {(financialStats?.cashFlow?.balance || 0) >= 0 ? 'Healthy' : 'Needs Attention'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Cash Flow Transactions */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Recent Cash Flow Activity</h3>
                <div className="space-y-3">
                  {(!financialStats?.recentTransactions || financialStats.recentTransactions.length === 0) ? (
                    <p className="text-slate-500 text-center py-8">No recent transactions available</p>
                  ) : (
                    financialStats.recentTransactions.slice(0, 8).map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-4 bg-slate-50/80 rounded-lg border">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                            transaction.type === 'income' 
                              ? 'bg-green-100 text-green-600' 
                              : 'bg-red-100 text-red-600'
                          }`}>
                            {transaction.type === 'income' ? (
                              <ArrowUpIcon className="w-5 h-5" />
                            ) : (
                              <ArrowDownIcon className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{transaction.description || 'Transaction'}</p>
                            <div className="flex items-center text-xs text-slate-500">
                              <span>{transaction.category || 'General'}</span>
                              <span className="mx-1">•</span>
                              <span>{new Date(transaction.date).toLocaleDateString()}</span>
                              <span className="mx-1">•</span>
                              <span className={`capitalize ${
                                transaction.status === 'completed' 
                                  ? 'text-green-600' 
                                  : transaction.status === 'pending' 
                                    ? 'text-yellow-600' 
                                    : 'text-red-600'
                              }`}>
                                {transaction.status || 'unknown'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-semibold ${
                            transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.type === 'income' ? '+' : '-'}R{(transaction.amount || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {(financialStats?.recentTransactions?.length || 0) > 8 && (
                  <div className="mt-4 text-center">
                    <Link
                      to="/transactions"
                      className="text-sm text-cyan-600 hover:text-cyan-500 font-medium"
                    >
                      View All Transactions →
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Cash Flow Health Indicators */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-slate-900 mb-4">Liquidity Health Indicators</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 border border-gray-200 rounded-lg">
                    <div className="text-2xl font-bold text-cyan-600">
                      {(((financialStats?.cashFlow?.incoming || 0) / Math.max(financialStats?.cashFlow?.outgoing || 1, 1)) * 1).toFixed(1)}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Cash Flow Ratio</p>
                    <p className="text-xs text-slate-500 mt-1">Inflow to Outflow</p>
                  </div>
                  
                  <div className="text-center p-4 border border-gray-200 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {Math.max(15, Math.floor((Math.abs(financialStats?.cashFlow?.balance || 0) / Math.max((financialStats?.cashFlow?.outgoing || 0) / 30, 1))))}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Days of Cash</p>
                    <p className="text-xs text-slate-500 mt-1">Operating runway</p>
                  </div>
                  
                  <div className="text-center p-4 border border-gray-200 rounded-lg">
                    <div className={`text-2xl font-bold ${
                      (financialStats?.cashFlow?.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {(financialStats?.cashFlow?.balance || 0) >= 0 ? '✓' : '⚠'}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Cash Status</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {(financialStats?.cashFlow?.balance || 0) >= 0 ? 'Positive' : 'Negative'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 